// frontend/src/components/MiceManagement.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ConfirmDialog } from './ConfirmDialog';
import PedigreeView from './PedigreeView';

import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { DataService } from '../services/DataService';
import { db } from '../services/db';
import { useDebounce } from '../hooks/useDebounce';
import { parseSearchQuery, applySearchParams } from '../utils/searchParser';
import { SearchHelp } from './SearchHelp';
import { validateDateOfBirth } from '../utils/validators';
import { useToast } from '../hooks/useToast';
import { SkeletonRow } from './SkeletonRow';
import MiceTable from './mice/MiceTable';
import { ExcelImport } from './mice/ExcelImport';
import MouseForm from './mice/MouseForm';
import MoveMouseModal from './mice/MoveMouseModal';
import { BulkActionBar, TransferModal } from './mice/BulkActions';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { useConfirm } from '../hooks/useConfirm';
import { useGeneticsOptions } from '../hooks/useGeneticsOptions';
import { MAX_LIST_LIMIT } from '../utils/apiConstants';


const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];


function MiceManagement({ navigateToSettings, activeView }) {
 // --- All State Declarations Go Here ---
 const { user, token, currentViewMode } = useAuth();
 const toast = useToast();
 const [mice, setMice] = useState([]);
 const [cages, setCages] = useState([]);
 const [labMembers, setLabMembers] = useState([]);
 const [loading, setLoading] = useState(false);
 // Phase 6: action outcomes are toasts. Two inline messages survive, for the
 // two reasons a toast is the wrong shape: `loadError` explains a page that is
 // otherwise blank (a toast would fade and leave the emptiness unexplained),
 // and `formError` belongs beside the fields it is about and must persist
 // while the user fixes them.
 const [loadError, setLoadError] = useState('');
 const [formError, setFormError] = useState('');
 const { locusOptions, alleleOptions, fetchGenotypeOptions } = useGeneticsOptions();
 const [strains, setStrains] = useState([]);
 const [unassignedResultsCount, setUnassignedResultsCount] = useState(0);
 const { confirm: showConfirm, confirmDialogProps } = useConfirm();
 const isFetchingRef = useRef(false);
 const fetchDataRef = useRef(null);
 const fetchAllActiveCagesRef = useRef(null);
 const cageSearchRef = useRef(null);

 // Pagination states for Mice
 const [currentPageMice, setCurrentPageMice] = useState(1);
 const [itemsPerPageMice, setItemsPerPageMice] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
 const [totalMiceCount, setTotalMiceCount] = useState(0);

 // Sorting states for Mice
 const [sortColumnMice, setSortColumnMice] = useState('created_at');
 const [sortOrderMice, setSortOrderMice] = useState('desc');

 // Search states for Mice
 const [searchQueryMice, setSearchQueryMice] = useState('');
 const debouncedSearchMice = useDebounce(searchQueryMice, 350);

 // Filter states for Mice
 const [filtersMice, setFiltersMice] = useState({
  sex: '',
  min_age_weeks: '',
  max_age_weeks: '',
  filter_genotype_locus: '',
  filter_genotype_allele: '',
  status: 'Active'
 });
 const [showFiltersMice, setShowFiltersMice] = useState(false);
 // open context menu id for the three-dot mouse action menu
 const [openMenuMouseId, setOpenMenuMouseId] = useState(null);

 const [mouseFormData, setMouseFormData] = useState(
  {
   physical_tag_input: '',
   sex: 'Male',
   genotype_data: [],
   currentLocusSelection: '',
   currentAlleleSelection: '',
   date_of_birth: '',
   copies: '',
   notes: '',
   strain_id: ''
  }
 );
 const [isEditing, setIsEditing] = useState(false);
 const [editedMouseId, setEditedMouseId] = useState(null);
 const [editingMouseArchived, setEditingMouseArchived] = useState(false);
 const [isSubmitting, setIsSubmitting] = useState(false);
 const [isExporting, setIsExporting] = useState(false);
 const [isCreatingCage, setIsCreatingCage] = useState(false);
 const [isConfirmingMove, setIsConfirmingMove] = useState(false);
 const [isBulkMoving, setIsBulkMoving] = useState(false);
 const [isTransferringMice, setIsTransferringMice] = useState(false);
 const [isSavingQuickItem, setIsSavingQuickItem] = useState(false);

 const [sharedCageId, setSharedCageId] = useState('');

 const [showNewCageForm, setShowNewCageForm] = useState(false);
 const [newCage, setNewCage] = useState({ display_cage_number: '', location: '' });

 const [isMovingMouse, setIsMovingMouse] = useState(false);
 const [movingMouseDetails, setMovingMouseDetails] = useState(null);
 const [newCageForMovingMouse, setNewCageForMovingMouse] = useState('');
 const [cageSearchQuery, setCageSearchQuery] = useState('');
 const [showCageDropdown, setShowCageDropdown] = useState(false);

 const [selectedMiceForBulkMove, setSelectedMiceForBulkMove] = useState([]);
 const [showBulkMoveCageSelector, setShowBulkMoveCageSelector] = useState(false);
 const [newCageForBulkMove, setNewCageForBulkMove] = useState('');

 // State for transfers
 const [showTransferModal, setShowTransferModal] = useState(false);
 const [transferRecipientId, setTransferRecipientId] = useState('');

 // State for Excel Import
 // Control visibility of the slide-out panel and what content to show
 const [showSlideOutPanel, setShowSlideOutPanel] = useState(false);
 const [slideOutContent, setSlideOutContent] = useState('mouse');
 const slideOutPanelRef = useRef(null);

 // Mobile view state
 const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
 const [isLoadingMore, setIsLoadingMore] = useState(false);

 const [isAddingLocus, setIsAddingLocus] = useState(false);
 const [isAddingAllele, setIsAddingAllele] = useState(false);
 const [isAddingStrain, setIsAddingStrain] = useState(false);
 const [newLocusName, setNewLocusName] = useState("");
 const [newAlleleName, setNewAlleleName] = useState("");
 const [newStrainName, setNewStrainName] = useState("");
 const [selectedMouseForPedigree, setSelectedMouseForPedigree] = useState(null);

 // --- End State Declarations ---

 // Check for mobile view on resize
 useEffect(() => {
  const handleResize = () => {
   setIsMobile(window.innerWidth <= 768);
  };
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
 }, []);

 useEffect(() => {
  if (showSlideOutPanel && slideOutPanelRef.current) {
    slideOutPanelRef.current.scrollTop = 0;
  }
 }, [showSlideOutPanel]);

 useEffect(() => {
  const handleEscapeKey = (event) => {
    if (event.key === 'Escape' && showSlideOutPanel) {
      handleCancelSlideOut();
    }
  };

  // Add event listener when slide-out is open
  if (showSlideOutPanel) {
    document.addEventListener('keydown', handleEscapeKey);
  }

  // Cleanup function to remove event listener
  return () => {
    document.removeEventListener('keydown', handleEscapeKey);
  };
 }, [showSlideOutPanel]);

 // Close the three-dot context menu when clicking outside it
 useEffect(() => {
  if (!openMenuMouseId) return;
  const close = () => setOpenMenuMouseId(null);
  document.addEventListener('click', close);
  return () => document.removeEventListener('click', close);
 }, [openMenuMouseId]);

 // Reset filters/search when user clicks the active Mice tab again
 useEffect(() => {
   const handleTabReset = (e) => {
     if (e.detail?.view === 'mice') {
       setSearchQueryMice('');
       setFiltersMice({ sex: '', min_age_weeks: '', max_age_weeks: '', filter_genotype_locus: '', filter_genotype_allele: '', status: 'Active' });
       setShowFiltersMice(false);
       setCurrentPageMice(1);
     }
   };
   window.addEventListener('squeakbase-tab-reset', handleTabReset);
   return () => window.removeEventListener('squeakbase-tab-reset', handleTabReset);
 }, []);


 // Keep refs pointing at the latest closures.
 useEffect(() => { fetchDataRef.current = fetchData; });
 useEffect(() => { fetchAllActiveCagesRef.current = fetchAllActiveCages; });

 // Re-fetch after any sync completes — silently (no skeleton flash).
 useEffect(() => {
  const onSync = () => {
   if (isFetchingRef.current) return;
   fetchDataRef.current?.(true);
   fetchAllActiveCagesRef.current?.();
  };
  window.addEventListener('squeakbase-sync-complete', onSync);
  return () => window.removeEventListener('squeakbase-sync-complete', onSync);
 }, []);

 // Re-fetch when user navigates to the Mice tab (not on initial mount).
 const prevActiveViewRef = useRef('');
 useEffect(() => {
  const prev = prevActiveViewRef.current;
  prevActiveViewRef.current = activeView;
  if (prev === activeView || activeView !== 'mice') return;
  if (isFetchingRef.current) return;
  fetchDataRef.current?.(true);
  fetchAllActiveCagesRef.current?.();
 }, [activeView]);

 // --- All Helper Functions, Event Handlers, and API Calls Go Here ---

  // Fetch all active cages once for dropdowns
  useEffect(() => {
    fetchAllActiveCages();
   fetchLabMembers();
  }, [token]);
   const fetchLabMembers = async () => {
     if (!token) return;
     try {
       const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/labs/members`);
       setLabMembers(response.data.members.filter(member => member.id !== user.id)); // Exclude current user
     } catch (err) {
       console.error('Failed to fetch lab members:', err);
     }
   };
  const fetchAllActiveCages = async () => {
    try {
      // Use local IndexedDB via DataService (populated by sync)
      const cagesData = await DataService.getCages();
      if (cagesData.length > 0) {
        setCages(cagesData);
        return;
      }
      // Fallback to API if local DB is empty (first load before sync)
      if (!token) return;
      const cagesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/cages?_limit=${MAX_LIST_LIMIT}&filter_status=Active`);
      setCages(cagesResponse.data.data);
    } catch (err) {
      console.error('Failed to fetch cages for dropdowns:', err);
    }
  };
 const handleTransferSelectedMice = () => {
   if (!navigator.onLine) {
     toast.warn('Transfers require an internet connection.');
     return;
   }
   if (selectedMiceForBulkMove.length === 0) {
     toast.warn('Please select at least one mouse to transfer.');
     return;
   }
   setFormError('');
   setTransferRecipientId('');
   setShowTransferModal(true);
 };

 const handleConfirmTransfer = async () => {
   if (isTransferringMice) return;
   if (!transferRecipientId) {
     setFormError('Please select a recipient.');
     return;
   }
   setFormError('');

   setIsTransferringMice(true);
   try {
     const payload = {
       mice_to_transfer: selectedMiceForBulkMove,
       recipient_user_id: transferRecipientId
     };

     const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/mice/transfer`, payload);
     toast.success(response.data.message);
     setShowTransferModal(false);
     setTransferRecipientId('');
     setSelectedMiceForBulkMove([]);
     // Sync local DB to reflect the transfer (archived mice + new copies)
     await DataService.sync();
     fetchData();
   } catch (err) {
     toast.error(err.response?.data?.error || 'Failed to transfer mice.');
     console.error('Transfer error:', err);
   } finally {
     setIsTransferringMice(false);
   }
 };
  const handleSaveNewStrain = async () => {
      if (isSavingQuickItem) return;
      if (newStrainName && newStrainName.trim()) {
          setIsSavingQuickItem(true);
          try {
              // The bulk endpoint returns the created items.
              const response = await axios.post(
                  `${process.env.REACT_APP_API_URL}/api/strains/bulk`,
                  { items: [{ strain_name: newStrainName.trim() }] }
              );

              // Get the ID of the newly created strain directly from the response.
              const newStrainId = response.data?.created_strains?.[0]?.id;

              if (newStrainId) {
                  // Now, update the mouse form data with the new strain's ID.
                  setMouseFormData(prev => ({ ...prev, strain_id: newStrainId }));
              }

              // Fetch fresh strains from API (bypass local cache which may be stale)
              const strainsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/strains?_limit=${MAX_LIST_LIMIT}`);
              const freshStrains = strainsResponse.data.data;
              // Update local IndexedDB cache so future reads are correct
              await db.strains.clear();
              await db.strains.bulkPut(freshStrains);
              setStrains(freshStrains);

              toast.success(`Strain "${newStrainName.trim()}" added successfully.`);
              setIsAddingStrain(false);
              setNewStrainName("");
          } catch (err) {
              toast.error(err.response?.data?.error || 'Failed to add new strain.');
          } finally {
              setIsSavingQuickItem(false);
          }
      }
  };

 const fetchData = async (silent = false) => {
  if (!user || !token) {
   return;
  }
  isFetchingRef.current = true;
  if (!silent) setLoading(true);
  // Silent re-fetches (sync, tab switch) need all accumulated pages to avoid duplicates
  const needsFullRefresh = silent && isMobile && currentPageMice > 1;
  const fetchPage = needsFullRefresh ? 1 : currentPageMice;
  const fetchLimit = needsFullRefresh ? currentPageMice * itemsPerPageMice : itemsPerPageMice;
  try {
   let backendSortColumn = sortColumnMice;
   let backendSortOrder = sortOrderMice;

   if (sortColumnMice === 'age') {
    backendSortColumn = 'date_of_birth';
   }

   const parsedSearch = parseSearchQuery(debouncedSearchMice);
   const miceParamsObj = new URLSearchParams({
    _page: fetchPage,
    _limit: fetchLimit,
    _sort: backendSortColumn,
    _order: backendSortOrder,
    ...(filtersMice.sex && { filter_sex: filtersMice.sex }),
    ...(filtersMice.cage_id && { filter_cage_id: filtersMice.cage_id }),
    ...(filtersMice.min_age_weeks && { min_age_weeks: filtersMice.min_age_weeks }),
    ...(filtersMice.max_age_weeks && { max_age_weeks: filtersMice.max_age_weeks }),
    ...(filtersMice.filter_genotype_locus && { filter_genotype_locus: filtersMice.filter_genotype_locus }),
    ...(filtersMice.filter_genotype_allele && { filter_genotype_allele: filtersMice.filter_genotype_allele }),
    ...(filtersMice.status && { filter_status: filtersMice.status })
   });
   applySearchParams(miceParamsObj, parsedSearch);
   const miceParams = miceParamsObj.toString();
   if (!navigator.onLine) {
     // Offline fallback: read from local IndexedDB via DataService
     const result = await DataService.getMice({
       search: parsedSearch.freeText,
       fieldFilters: parsedSearch.fieldFilters,
       status: filtersMice.status,
       filter_sex: filtersMice.sex,
       filter_cage_id: filtersMice.cage_id,
       min_age_weeks: filtersMice.min_age_weeks,
       max_age_weeks: filtersMice.max_age_weeks,
       filter_genotype_locus: filtersMice.filter_genotype_locus,
       filter_genotype_allele: filtersMice.filter_genotype_allele,
       sortColumn: backendSortColumn,
       sortOrder: backendSortOrder,
       page: fetchPage,
       limit: fetchLimit,
     });
     if (isMobile && currentPageMice > 1 && !needsFullRefresh) {
       setMice(prev => [...prev, ...result.data]);
     } else {
       setMice(result.data);
     }
     setTotalMiceCount(result.totalCount);
     setLoadError('');
   } else {
     const miceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/mice?${miceParams}`);
     if (isMobile && currentPageMice > 1 && !needsFullRefresh) {
       setMice(prev => [...prev, ...miceResponse.data.data]);
     } else {
       setMice(miceResponse.data.data);
     }
     setTotalMiceCount(miceResponse.data.totalCount);
     setLoadError('');
   }
  } catch (err) {
   setLoadError('Failed to load data');
   console.error('Fetch error:', err);
  } finally {
   isFetchingRef.current = false;
   setLoading(false);
  }
 };

 // On mobile, reset to page 1 when filters/search/sort change
 useEffect(() => {
  if (isMobile && currentPageMice !== 1) {
   setCurrentPageMice(1);
  }
 }, [isMobile, sortColumnMice, sortOrderMice, debouncedSearchMice, filtersMice]);

 useEffect(() => {
  if (user && token) {
   if (isMobile && currentPageMice > 1) {
    setIsLoadingMore(true);
    fetchData(true).finally(() => setIsLoadingMore(false));
   } else {
    fetchData();
   }
  }
 }, [user, token, currentPageMice, itemsPerPageMice, sortColumnMice, sortOrderMice, debouncedSearchMice, filtersMice]);

 useEffect(() => {
  if (token) {
   fetchStrains();
  }
 }, [token]);

 const fetchStrains = async () => {
    try {
        // Try local DB first (populated by sync)
        const localStrains = await DataService.getStrains();
        if (localStrains.length > 0) {
            setStrains(localStrains);
            return;
        }
        // Fallback to API
        if (!token) return;
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/strains?_limit=${MAX_LIST_LIMIT}`);
        setStrains(response.data.data);
    } catch (err) {
        console.error('Failed to fetch strains:', err);
    }
 };

 useEffect(() => {
    if (!user?.has_tyx_integration) return;

    const fetchUnassignedCount = async () => {
        if (!token) return;
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/transnetyx/results`);
            setUnassignedResultsCount(res.data.length);
        } catch (err) {
            console.error('Failed to fetch unassigned Transnetyx results count:', err);
            setUnassignedResultsCount(0); // Fail silently
        }
    };

    fetchUnassignedCount(); // Fetch on component mount
    const intervalId = setInterval(fetchUnassignedCount, 15000); // And poll every 15 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
}, [token, user?.has_tyx_integration]);

 // Close the cage search dropdown when the user clicks outside the combobox wrapper.
 // We listen on the whole document; if the click target isn't inside cageSearchRef
 // (checked via Node.contains), we hide the list.
 useEffect(() => {
  const handleClickOutside = (e) => {
   if (cageSearchRef.current && !cageSearchRef.current.contains(e.target)) {
    setShowCageDropdown(false);
   }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
 }, []);

 const resetForm = () => {
  setMouseFormData(
   {
    physical_tag_input: '',
    sex: 'Male',
    genotype_data: [],
    currentLocusSelection: '',
    currentAlleleSelection: '',
    date_of_birth: '',
    copies: 1,
    notes: '',
    strain_id: ''
   }
  );
  setIsEditing(false);
  setEditedMouseId(null);
  setSharedCageId('');
  setShowNewCageForm(false);
  setNewCage({ display_cage_number: '', location: '' }); // Reset newCage form too

  setIsMovingMouse(false);
  setMovingMouseDetails(null);
  setNewCageForMovingMouse('');
  setCageSearchQuery('');
  setShowCageDropdown(false);
  setSelectedMiceForBulkMove([]);
  setShowBulkMoveCageSelector(false);
  setNewCageForBulkMove('');


  setShowSlideOutPanel(false);
  setSlideOutContent('mouse');
  setFormError('');
 };

 const resetOtherForms = () => {
  setIsEditing(false);
  setEditedMouseId(null);
  setMouseFormData({ ...mouseFormData, physical_tag_input: '', genotype_data: [] });
  setSharedCageId('');
  setShowNewCageForm(false);
  setNewCage({ display_cage_number: '', location: '' }); // Reset newCage form too
  setIsMovingMouse(false);
  setMovingMouseDetails(null);
  setNewCageForMovingMouse('');
  setSelectedMiceForBulkMove([]);
  setShowBulkMoveCageSelector(false);
  setNewCageForBulkMove('');
  setShowSlideOutPanel(false);
  setSlideOutContent('mouse');
 };

 const handleInputChange = (e) => {
  const { name, value } = e.target;
  setMouseFormData({ ...mouseFormData, [name]: value });
 };

 const handleGenotypeSelectChange = (e) => {
  const { name, value } = e.target;
  setMouseFormData({ ...mouseFormData, [name]: value });
 };

 const handleSaveNewLocus = async () => {
    if (isSavingQuickItem) return;
    if (newLocusName && newLocusName.trim()) {
      setIsSavingQuickItem(true);
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/locus/bulk`, { items: [{ locus_name: newLocusName.trim() }] });
        await DataService.sync({ forceStaticRefresh: true }); // Pull new locus into Dexie
        await fetchGenotypeOptions(); // Refresh options from Dexie
        setMouseFormData(prev => ({ ...prev, currentLocusSelection: newLocusName.trim() }));
        toast.success(`Locus "${newLocusName.trim()}" added successfully.`);
        setIsAddingLocus(false);
        setNewLocusName("");
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to add new locus.');
      } finally {
        setIsSavingQuickItem(false);
      }
    }
  };

  const handleSaveNewAllele = async () => {
    if (isSavingQuickItem) return;
    if (newAlleleName && newAlleleName.trim()) {
      setIsSavingQuickItem(true);
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/allele/bulk`, { items: [{ allele_name: newAlleleName.trim() }] });
        await DataService.sync({ forceStaticRefresh: true }); // Pull new allele into Dexie
        await fetchGenotypeOptions(); // Refresh options from Dexie
        setMouseFormData(prev => ({ ...prev, currentAlleleSelection: newAlleleName.trim() }));
        toast.success(`Allele "${newAlleleName.trim()}" added successfully.`);
        setIsAddingAllele(false);
        setNewAlleleName("");
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to add new allele.');
      } finally {
        setIsSavingQuickItem(false);
      }
    }
  };

 const handleAddGenotypePair = () => {
  const { currentLocusSelection, currentAlleleSelection, genotype_data } = mouseFormData;

  if (currentLocusSelection && currentAlleleSelection) {
   const newPair = {
    locus_name: currentLocusSelection,
    allele_name: currentAlleleSelection
   };

   const isDuplicate = genotype_data.some(
    (pair) => pair.locus_name === newPair.locus_name && pair.allele_name === newPair.allele_name
   );

   if (!isDuplicate) {
    setMouseFormData(prevData => ({
      ...prevData,
      genotype_data: [...prevData.genotype_data, newPair],
      currentLocusSelection: '',
      currentAlleleSelection: ''
    }));
    setFormError('');
    toast.success('Genotype pair added.');
   } else {
    setFormError('This genotype pair has already been added to this mouse.');
   }
  } else {
   setFormError('Please select both a locus and an allele to add a genotype pair.');
  }
 };

 const handleRemoveGenotypePair = (pairIndexToRemove) => {
  setMouseFormData(prevData => ({
    ...prevData,
    genotype_data: prevData.genotype_data.filter((_, idx) => idx !== pairIndexToRemove)
  }));
 };

 const handleSubmit = async (e) => {
  e.preventDefault();
  if (isSubmitting) return;
  setIsSubmitting(true);
  setFormError('');

  if (!isEditing && !mouseFormData.date_of_birth) {
    setFormError('Date of Birth is a required field.');
    setIsSubmitting(false);
    return;
  }

  // Shared validation (C14). The future-date check lived here and nowhere else,
  // so the same mistake sailed through the cage and litter forms; it now also
  // catches a mistyped year in the other direction, which used to be stored
  // silently and show up later as an absurd age.
  const mouseError = validateDateOfBirth(mouseFormData.date_of_birth, { fieldName: 'Date of Birth' });
  if (mouseError) {
    setFormError(mouseError);
    setIsSubmitting(false);
    return;
  }

  try {
   let successMsg = '';

   if (isEditing) {
    // Check if we are editing multiple mice
    if (Array.isArray(editedMouseId)) {
     // Bulk edit: requires online connection, uses API directly
     const updates = {};
     if (mouseFormData.sex) updates.sex = mouseFormData.sex;
     if (mouseFormData.date_of_birth) updates.date_of_birth = mouseFormData.date_of_birth;
     if (mouseFormData.notes) updates.notes = mouseFormData.notes;
     if (mouseFormData.strain_id) updates.strain_id = mouseFormData.strain_id;
     if (sharedCageId) updates.cage_id = sharedCageId;
     if (mouseFormData.genotype_data.length > 0) updates.genotype_data = mouseFormData.genotype_data;

     const payload = {
      mouse_ids: editedMouseId,
      updates: updates
     };
     await axios.put(`${process.env.REACT_APP_API_URL}/api/mice/bulk-update`, payload);
     await DataService.sync(); // Reflect bulk update in local DB
     successMsg = `${editedMouseId.length} mice updated successfully!`;
    } else {
     // Single mouse edit - offline supported via DataService
     const payload = {
      id: editedMouseId,
      physical_tag: mouseFormData.physical_tag_input,
      sex: mouseFormData.sex,
      genotype_data: mouseFormData.genotype_data,
      cage_id: sharedCageId || null,
      date_of_birth: mouseFormData.date_of_birth,
      notes: mouseFormData.notes,
      strain_id: mouseFormData.strain_id
     };

     if (payload.physical_tag && payload.physical_tag.includes(',')) {
      setFormError('Physical tag cannot contain commas when editing a single mouse.');
      setIsSubmitting(false);
      return;
     }

     await DataService.saveMouse(payload);
     successMsg = 'Mouse updated successfully!';
    }
   } else {
    // Create new mice - offline supported via DataService
    let miceToSubmit = [];
    const basePhysicalTags = mouseFormData.physical_tag_input.split(',').map(tag => tag.trim()).filter(tag => tag);
    const numCopies = Math.max(1, parseInt(mouseFormData.copies, 10) || 1);

    if (basePhysicalTags.length === 0) {
      basePhysicalTags.push('');
    }

    basePhysicalTags.forEach(tag => {
     for (let i = 0; i < numCopies; i++) {
       miceToSubmit.push({
         physical_tag: tag === '' ? null : tag,
         sex: mouseFormData.sex,
         genotype_data: mouseFormData.genotype_data,
         cage_id: sharedCageId || null,
         date_of_birth: mouseFormData.date_of_birth,
         notes: mouseFormData.notes,
         strain_id: mouseFormData.strain_id
       });
     }
    });

    if (miceToSubmit.length === 0) {
     setFormError('No mouse data to submit. Please fill at least one row or provide physical tags/genotypes.');
     setIsSubmitting(false);
     return;
    }

    for (const mouse of miceToSubmit) {
      await DataService.saveMouse(mouse);
    }
    successMsg = `${miceToSubmit.length} mouse(s) added successfully!`;
   }

   await DataService.sync();
   resetForm();
   fetchData();
   fetchAllActiveCages();
   toast.success(successMsg);
  } catch (err) {
   toast.error(err.response?.data?.error || err.message || `Failed to ${isEditing ? 'update' : 'add'} mouse(s)`);
   console.error('Mouse operation error:', err);
  } finally {
   setIsSubmitting(false);
  }
 };

 const handleEditMouse = (mouse) => {
  if (!isMobile && !selectedMiceForBulkMove.includes(mouse.id)) {
    setSelectedMiceForBulkMove([mouse.id]);
  }

  setIsEditing(true);
  setEditedMouseId(mouse.id);
  setEditingMouseArchived(mouse.status === 'Archived');
  setMouseFormData(
   {
    physical_tag_input: mouse.physical_tag || '',
    sex: mouse.sex || 'Male',
    genotype_data: mouse.genotype || [],
    currentLocusSelection: '',
    currentAllelleSelection: '',
    date_of_birth: mouse.date_of_birth ? mouse.date_of_birth.split('T')[0] : '',
    copies: 1,
    notes: mouse.notes || '',
    strain_id: mouse.strain_id || ''
   }
  );
  setSharedCageId(mouse.cage_id || '');
  setShowNewCageForm(false);
  setNewCage({ display_cage_number: '', location: '' });
  setIsMovingMouse(false);
  setMovingMouseDetails(null);
  setNewCageForMovingMouse('');
  setShowBulkMoveCageSelector(false);
  setNewCageForBulkMove('');

  // Keep selectedMiceForBulkMove intact - don't reset it
  setSlideOutContent('mouse');
  setShowSlideOutPanel(true);
  setFormError('');
 };

 const handleNewCageSubmit = async (e) => {
  e.preventDefault();
  if (isCreatingCage) return;
  setIsCreatingCage(true);
  try {
   const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/cages`, {
     display_cage_number: newCage.display_cage_number,
     location: newCage.location,
     capacity: 5
   });

   // Sync to pull the new cage into local DB, then refresh dropdown
   await DataService.sync();
   await fetchAllActiveCages();

   setSharedCageId(response.data.id);
   setNewCageForBulkMove(response.data.id);
   setNewCageForMovingMouse(response.data.id);

   setNewCage({ display_cage_number: '', location: '' });
   setShowNewCageForm(false);
   toast.success('Cage created successfully!');
  } catch (err) {
   toast.error(err.response?.data?.error || 'Failed to create cage');
   console.error('Create cage error:', err);
  } finally {
   setIsCreatingCage(false);
  }
 };

 // C11: undo an accidental archive.
 const handleUndoArchiveMouse = async (mouseId, mouseTag) => {
    try {
        await DataService.restoreMouse(mouseId);
        fetchData();   // re-fetch so the restored row comes back into the list
        fetchAllActiveCages();
        toast.success(`Mouse ${mouseTag || mouseId} restored.`);
    } catch (err) {
        toast.error('Could not undo the archive — the mouse is still archived.');
        console.error('Undo archive error:', err);
    }
 };

 const handleDeleteMouse = async (mouseId, mouseTag) => {
    // Copy corrected with C11: this IS undoable now, and telling the user it is
    // not was the reason an accidental archive turned into a support request.
    const isConfirmed = await showConfirm(
        `Archive mouse ${mouseTag || mouseId}? It will no longer appear in active lists. You can undo this straight away, or restore it later from the archived view.`,
        { confirmLabel: 'Yes, Archive', danger: true }
    );

    if (!isConfirmed) return;

    setFormError('');
    resetOtherForms();
    try {
        // Optimistic update: remove from list immediately so fetchData() racing
        // with the async sync() doesn't cause the mouse to reappear.
        setMice(prev => prev.filter(m => m.id !== mouseId));
        setTotalMiceCount(prev => Math.max(0, prev - 1));
        await DataService.archiveMouse(mouseId);
        fetchAllActiveCages();
        toast.withAction(
            `Mouse ${mouseTag || mouseId} archived.`,
            { label: 'Undo', onAction: () => handleUndoArchiveMouse(mouseId, mouseTag) }
        );
    } catch (err) {
        toast.error(err.message || 'Failed to archive mouse.');
        console.error('Archive mouse error:', err);
    }
};
 
 const handleDoubleClick = (mouse) => {
  handleEditMouse(mouse);
 };

 const handleMoveMouse = (mouse) => {
  if (!selectedMiceForBulkMove.includes(mouse.id)) {
   setSelectedMiceForBulkMove([mouse.id]);
  }
  setIsEditing(false);
  setEditedMouseId(null);
  setMouseFormData({ ...mouseFormData, physical_tag_input: '', genotype_data: [] });
  setSharedCageId('');
  setShowNewCageForm(false);
  setNewCage({ display_cage_number: '', location: '' });
  setShowBulkMoveCageSelector(false);
  setNewCageForBulkMove('');

  setIsMovingMouse(true);
  setMovingMouseDetails(mouse);
  setNewCageForMovingMouse(mouse.cage_id || '');
  setCageSearchQuery('');
  setShowCageDropdown(false);
  setSlideOutContent('move');
  setShowSlideOutPanel(true);
  setFormError('');
 };

 const handleConfirmMove = async (e) => {
  e.preventDefault();
  if (isConfirmingMove) return;
  setFormError('');

  if (!movingMouseDetails || !newCageForMovingMouse) {
   setFormError('Please select a mouse and a destination cage.');
   return;
  }

  setIsConfirmingMove(true);
  try {
   // DataService handles offline move (local update + queued sync)
   await DataService.saveMouse({
    id: movingMouseDetails.id,
    physical_tag: movingMouseDetails.physical_tag,
    sex: movingMouseDetails.sex,
    genotype_data: movingMouseDetails.genotype,
    date_of_birth: movingMouseDetails.date_of_birth,
    status: movingMouseDetails.status,
    cage_id: newCageForMovingMouse
   });
   await DataService.sync();
   resetForm();
   fetchData();
   fetchAllActiveCages();
   toast.success(`Mouse ${movingMouseDetails.physical_tag || movingMouseDetails.mouse_id} successfully moved!`);
  } catch (err) {
   toast.error(err.message || 'Failed to move mouse.');
   console.error('Move mouse error:', err);
  } finally {
   setIsConfirmingMove(false);
  }
 };

 const handleCancelMove = () => {
  resetForm();
 };

 const handleMouseCardSelect = (mouseId) => {
  setSelectedMiceForBulkMove(prevSelected => {
   const isSelected = prevSelected.includes(mouseId);
   if (isSelected) {
    return prevSelected.filter(id => id !== mouseId);
   } else {
    return [...prevSelected, mouseId];
   }
  });
 };

 const handleMoveSelectedMice = () => {
  if (selectedMiceForBulkMove.length === 0) {
   toast.warn('Please select at least one mouse to move.');
   return;
  }

  setIsEditing(false);
  setEditedMouseId(null);
  setMouseFormData({ ...mouseFormData, physical_tag_input: '', genotype_data: [] });
  setSharedCageId('');
  setShowNewCageForm(false);
  setNewCage({ display_cage_number: '', location: '' });
  setIsMovingMouse(false);
  setMovingMouseDetails(null);
  setNewCageForMovingMouse('');

  setShowBulkMoveCageSelector(true);
  setNewCageForBulkMove('');
  setCageSearchQuery('');
  setShowCageDropdown(false);
  setSlideOutContent('bulkMove');
  setShowSlideOutPanel(true);
  setFormError('');
 };

 const handleConfirmBulkMove = async (e) => {
  e.preventDefault();
  if (isBulkMoving) return;
  setFormError('');

  if (selectedMiceForBulkMove.length === 0 || !newCageForBulkMove) {
   setFormError('Please select mice and a destination cage.');
   return;
  }

  setIsBulkMoving(true);
  try {
   await DataService.bulkMoveMice(selectedMiceForBulkMove, newCageForBulkMove);
   await DataService.sync();
   resetForm();
   fetchData();
   fetchAllActiveCages();
   toast.success(`${selectedMiceForBulkMove.length} mouse(s) successfully moved!`);
  } catch (err) {
   toast.error(err.message || 'Failed to move selected mice.');
   console.error('Bulk move error:', err);
  } finally {
   setIsBulkMoving(false);
  }
 };

 const handleCancelMultiSelect = () => {
   resetForm();
 };

 const handleEditSelectedMice = () => {
  if (selectedMiceForBulkMove.length === 0) {
   toast.warn('Please select at least one mouse to edit.');
   return;
  }

  const selectedMiceData = mice.filter(mouse => selectedMiceForBulkMove.includes(mouse.id));

  if (selectedMiceForBulkMove.length === 1) {
   handleEditMouse(selectedMiceData[0]);
  } else {
   // Handle multiple mice editing
   const allSame = (prop) => selectedMiceData.every(mouse => mouse[prop] === selectedMiceData[0][prop]);

   setMouseFormData({
    physical_tag_input: 'Multiple Values',
    sex: allSame('sex') ? selectedMiceData[0].sex : '',
    genotype_data: [], // Genotypes are too complex to bulk-edit
    currentLocusSelection: '',
    currentAlleleSelection: '',
    date_of_birth: allSame('date_of_birth') ? (selectedMiceData[0].date_of_birth ? selectedMiceData[0].date_of_birth.split('T')[0] : '') : '',
    copies: 1, // Not applicable for editing
    notes: allSame('notes') ? selectedMiceData[0].notes : '',
    strain_id: allSame('strain_id') ? selectedMiceData[0].strain_id : '',
   });

   setSharedCageId(allSame('cage_id') ? selectedMiceData[0].cage_id : '');
   setIsEditing(true);
   setEditedMouseId(selectedMiceForBulkMove); // Store array of IDs
   setEditingMouseArchived(selectedMiceData.some(m => m.status === 'Archived'));
   setSlideOutContent('mouse');
   setShowSlideOutPanel(true);
   setFormError('');
  }
 };

 const handleRestoreMouse = async (mouseId, mouseTag) => {
   const isConfirmed = await showConfirm(
       `Are you sure you want to restore mouse ${mouseTag || mouseId}?`,
       { confirmLabel: 'Yes, Restore' }
   );

   if (!isConfirmed) return;

   setFormError('');
   resetOtherForms();

   try {
   await axios.put(`${process.env.REACT_APP_API_URL}/api/mice/${mouseId}/restore`, {});
   await DataService.sync();
   toast.success(`Mouse ${mouseTag || mouseId} restored successfully!`);
   fetchData();
  } catch (err) {
   toast.error(err.response?.data?.error || 'Failed to restore mouse.');
   console.error('Restore mouse error:', err);
  }
};
 const handleRestoreSelectedMice = async () => {
   if (selectedMiceForBulkMove.length === 0) {
     toast.warn('Please select at least one mouse to restore.');
     return;
      }

   const isConfirmed = await showConfirm(
       `Are you sure you want to restore ${selectedMiceForBulkMove.length} selected mouse(s)?`,
       { confirmLabel: 'Yes, Restore' }
   );

   if (!isConfirmed) return;

   try {
     await axios.put(
         `${process.env.REACT_APP_API_URL}/api/mice/bulk-restore`,
         { mouse_ids: selectedMiceForBulkMove }
     );
     await DataService.sync();
     toast.success(`${selectedMiceForBulkMove.length} mouse(s) restored successfully!`);
     setSelectedMiceForBulkMove([]);
     fetchData();
    } catch (err) {
     toast.error(err.response?.data?.error || 'Failed to restore selected mice.');
     console.error('Bulk restore error:', err);
    }
};

 const handleDeleteSelectedMice = async () => {
    if (selectedMiceForBulkMove.length === 0) {
        toast.warn('Please select at least one mouse to archive.');
        return;
    }

    const isConfirmed = await showConfirm(
        `Archive ${selectedMiceForBulkMove.length} selected mouse(s)? They will no longer appear in active lists. You can restore them later from the Archived Mice view.`,
        { confirmLabel: 'Yes, Archive', danger: true }
    );

    if (!isConfirmed) return;

    setFormError('');
    resetOtherForms();

    try {
        await axios.put(
            `${process.env.REACT_APP_API_URL}/api/mice/bulk-archive`,
            { mouse_ids: selectedMiceForBulkMove }
        );
        await DataService.sync();
        toast.success(`${selectedMiceForBulkMove.length} mouse(s) archived successfully!`);
        setSelectedMiceForBulkMove([]);
        fetchData();
        fetchAllActiveCages();
    } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to archive selected mice.');
        console.error('Bulk archive error:', err);
    }
};

 const handleSortMice = (column) => {
  let newSortOrder;
  if (sortColumnMice === column) {
   newSortOrder = sortOrderMice === 'asc' ? 'desc' : 'asc';
  } else {
   newSortOrder = 'asc';
  }

  setSortColumnMice(column);
  setSortOrderMice(newSortOrder);
  setCurrentPageMice(1);
 };

 const handleSearchMice = (e) => {
  setSearchQueryMice(e.target.value);
  setCurrentPageMice(1);
 };

 const clearSearchMice = () => {
  setSearchQueryMice('');
  setCurrentPageMice(1);
 };

 const handleFilterChangeMice = (filterType, value) => {
  setFiltersMice(prev => ({
   ...prev,
   [filterType]: value
  }));
  setCurrentPageMice(1);
 };

 const clearFiltersMice = () => {
  setFiltersMice({
   sex: '',
   min_age_weeks: '',
   max_age_weeks: '',
   filter_genotype_locus: '',
   filter_genotype_allele: '',
   status: 'Active'
  });
  setCurrentPageMice(1);
 };

  const hasActiveFiltersMice = Object.entries(filtersMice).some(([key, value]) => {
    if (key === 'status') {
      return value !== 'Active';
    }
    return value !== '';
  });

 const handlePageChangeMice = (pageNumber) => {
  setCurrentPageMice(pageNumber);
 };

 const handleItemsPerPageChangeMice = (e) => {
  setItemsPerPageMice(parseInt(e.target.value, 10));
  setCurrentPageMice(1);
 };

 const totalPagesMice = Math.ceil(totalMiceCount / itemsPerPageMice);

 const handleLoadMoreMice = useCallback(() => {
  if (currentPageMice < totalPagesMice) {
   setCurrentPageMice(prev => prev + 1);
  }
 }, [currentPageMice, totalPagesMice]);

 useInfiniteScroll({
  enabled: isMobile && activeView === 'mice',
  hasMore: currentPageMice < totalPagesMice,
  loading: loading || isLoadingMore,
  onLoadMore: handleLoadMoreMice,
 });


  const handleExportExcel = async () => {
  if (isExporting) return;
  setIsExporting(true);
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/export`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error('Export failed');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lab-export-${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error('Export failed. Please try again.');
  } finally {
    setIsExporting(false);
  }
 };

 const handleShowAddMouseForm = () => {
  setIsEditing(false);
  setMouseFormData({ physical_tag_input: '', sex: 'Male', genotype_data: [], currentLocusSelection: '', currentAlleleSelection: '', date_of_birth: '', copies: 1 });
  setSharedCageId('');
  setSlideOutContent('mouse');
  setShowSlideOutPanel(true);
  setFormError('');
 };

 const handleShowImportForm = () => {
  resetOtherForms();
  setSlideOutContent('import');
  setShowSlideOutPanel(true);
 };

 const handleCancelSlideOut = () => {
  setShowSlideOutPanel(false);
  setIsEditing(false);
  setEditedMouseId(null);
  setEditingMouseArchived(false);
  setIsMovingMouse(false);
  setMovingMouseDetails(null);
  setNewCageForMovingMouse('');
 };

 const savedScrollRef = useRef(0);
 const handleViewPedigree = (mouseId) => {
    const wrapper = document.querySelector('.swipe-content-wrapper');
    if (wrapper) savedScrollRef.current = wrapper.scrollTop;
    setSelectedMouseForPedigree(mouseId);
 };

 const renderSlideOutContent = () => {
  switch (slideOutContent) {
   case 'mouse':
    return (
     <MouseForm
      isEditing={isEditing}
      editedMouseId={editedMouseId}
      editingMouseArchived={editingMouseArchived}
      mouseFormData={mouseFormData}
      handleInputChange={handleInputChange}
      handleGenotypeSelectChange={handleGenotypeSelectChange}
      cages={cages}
      cageSearchRef={cageSearchRef}
      cageSearchQuery={cageSearchQuery}
      setCageSearchQuery={setCageSearchQuery}
      showCageDropdown={showCageDropdown}
      setShowCageDropdown={setShowCageDropdown}
      sharedCageId={sharedCageId}
      setSharedCageId={setSharedCageId}
      showNewCageForm={showNewCageForm}
      setShowNewCageForm={setShowNewCageForm}
      newCage={newCage}
      setNewCage={setNewCage}
      onCreateCage={handleNewCageSubmit}
      isCreatingCage={isCreatingCage}
      strains={strains}
      locusOptions={locusOptions}
      alleleOptions={alleleOptions}
      isAddingStrain={isAddingStrain}
      setIsAddingStrain={setIsAddingStrain}
      newStrainName={newStrainName}
      setNewStrainName={setNewStrainName}
      onSaveNewStrain={handleSaveNewStrain}
      isAddingLocus={isAddingLocus}
      setIsAddingLocus={setIsAddingLocus}
      newLocusName={newLocusName}
      setNewLocusName={setNewLocusName}
      onSaveNewLocus={handleSaveNewLocus}
      isAddingAllele={isAddingAllele}
      setIsAddingAllele={setIsAddingAllele}
      newAlleleName={newAlleleName}
      setNewAlleleName={setNewAlleleName}
      onSaveNewAllele={handleSaveNewAllele}
      isSavingQuickItem={isSavingQuickItem}
      onAddGenotypePair={handleAddGenotypePair}
      onRemoveGenotypePair={handleRemoveGenotypePair}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      onCancel={handleCancelSlideOut}
      error={formError}
     />
    );

   case 'move':
    return (
     <MoveMouseModal
      title={`Move Mouse: ${movingMouseDetails?.physical_tag || movingMouseDetails?.mouse_id}`}
      cages={cages}
      cageSearchRef={cageSearchRef}
      cageSearchQuery={cageSearchQuery}
      setCageSearchQuery={setCageSearchQuery}
      showCageDropdown={showCageDropdown}
      setShowCageDropdown={setShowCageDropdown}
      selectedCageId={newCageForMovingMouse}
      setSelectedCageId={setNewCageForMovingMouse}
      showNewCageForm={showNewCageForm}
      setShowNewCageForm={setShowNewCageForm}
      newCage={newCage}
      setNewCage={setNewCage}
      onCreateCage={handleNewCageSubmit}
      isCreatingCage={isCreatingCage}
      onSubmit={handleConfirmMove}
      isSubmitting={isConfirmingMove}
      submitLabel="Confirm Move"
      submittingLabel="Moving..."
      onCancel={handleCancelSlideOut}
      error={formError}
     />
    );

   case 'bulkMove':
    return (
     <MoveMouseModal
      title={`Assign ${selectedMiceForBulkMove.length} Selected Mouse(s)`}
      fieldLabel="Assign to Cage:"
      cages={cages}
      cageSearchRef={cageSearchRef}
      cageSearchQuery={cageSearchQuery}
      setCageSearchQuery={setCageSearchQuery}
      showCageDropdown={showCageDropdown}
      setShowCageDropdown={setShowCageDropdown}
      selectedCageId={newCageForBulkMove}
      setSelectedCageId={setNewCageForBulkMove}
      showNewCageForm={showNewCageForm}
      setShowNewCageForm={setShowNewCageForm}
      newCage={newCage}
      setNewCage={setNewCage}
      onCreateCage={handleNewCageSubmit}
      isCreatingCage={isCreatingCage}
      onSubmit={handleConfirmBulkMove}
      isSubmitting={isBulkMoving}
      submitLabel="Confirm Bulk Move"
      submittingLabel="Moving..."
      onCancel={handleCancelSlideOut}
      error={formError}
     />
    );

   case 'import':
    return (
      <ExcelImport
        onImportComplete={() => { fetchData(); resetForm(); }}
        onCancel={handleCancelSlideOut}
      />
    );

   default:
    return null;
  }
 };

 if (loadError) return <div className="error">{loadError}</div>;

 if (selectedMouseForPedigree) {
    return (
      <PedigreeView
        mouseId={selectedMouseForPedigree}
        onBack={() => {
          setSelectedMouseForPedigree(null);
          requestAnimationFrame(() => {
            const wrapper = document.querySelector('.swipe-content-wrapper');
            if (wrapper) wrapper.scrollTop = savedScrollRef.current;
          });
        }}
      />
    );
  }

 return (
  <div className="main-content mouse-management-grid">
   {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
   <div className="mice-section">
    <div className="sticky-toolbar">
    {/* Search Bar + Filter toggle */}
    <div className="search-bar">
     <input
      type="text"
      placeholder="Search... (try cage:Blue-42 or strain:C57)"
      value={searchQueryMice}
      onChange={handleSearchMice}
     />
     <SearchHelp />
     {searchQueryMice && (
      <button
       onClick={clearSearchMice}
       style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#666', width: 'auto' }}
       title="Clear search"
      >
       ✕
      </button>
     )}
     <button
      className={`filter-toggle-btn${hasActiveFiltersMice ? ' filter-toggle-btn--active' : ''}`}
      onClick={() => setShowFiltersMice(v => !v)}
      title="Toggle filters"
     >
      <span className="filter-icon"><span /><span /><span /></span>
      {hasActiveFiltersMice && <span className="filter-active-dot" />}
     </button>
    </div>

    {/* Filter Controls — collapsible */}
    {showFiltersMice && (
    <div className="filter-controls">
     <select
      value={filtersMice.sex}
      onChange={(e) => handleFilterChangeMice('sex', e.target.value)}
     >
      <option value="">All Sexes</option>
      <option value="Male">Male</option>
      <option value="Female">Female</option>
     </select>

     <select value={filtersMice.status} onChange={(e) => handleFilterChangeMice('status', e.target.value)}>
       <option value="Active">Active Mice</option>
       <option value="Archived">Archived Mice</option>
       <option value="">All Mice</option>
     </select>

     <input
       type="number"
       placeholder="Min Age (weeks)"
       value={filtersMice.min_age_weeks}
       onChange={(e) => handleFilterChangeMice('min_age_weeks', e.target.value)}
     />
     <input
       type="number"
       placeholder="Max Age (weeks)"
       value={filtersMice.max_age_weeks}
       onChange={(e) => handleFilterChangeMice('max_age_weeks', e.target.value)}
     />

     <select
       value={filtersMice.filter_genotype_locus}
       onChange={(e) => handleFilterChangeMice('filter_genotype_locus', e.target.value)}
     >
       <option value="">Filter by Locus</option>
       {locusOptions.map(locus => (
         <option key={locus.id} value={locus.locus_name}>
           {locus.locus_name}
         </option>
       ))}
     </select>
     <select
       value={filtersMice.filter_genotype_allele}
       onChange={(e) => handleFilterChangeMice('filter_genotype_allele', e.target.value)}
     >
       <option value="">Filter by Allele</option>
       {alleleOptions.map(allele => (
         <option key={allele.id} value={allele.allele_name}>
           {allele.allele_name}
         </option>
       ))}
     </select>

     {hasActiveFiltersMice && (
      <button
       onClick={() => { clearFiltersMice(); }}
       style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '20px', padding: '0.5rem 1rem', fontSize: '0.9rem', cursor: 'pointer' }}
       title="Clear all filters"
      >
       Clear Filters
      </button>
     )}
    </div>
    )}

    {/* Desktop Action Buttons */}
    {!isMobile && (
     <BulkActionBar
      selectedCount={selectedMiceForBulkMove.length}
      isArchivedView={filtersMice.status === 'Archived'}
      canTransfer={Boolean(user.lab_id) && labMembers.length > 0}
      isExporting={isExporting}
      unassignedResultsCount={unassignedResultsCount}
      onAddNewMouse={handleShowAddMouseForm}
      onEditSelected={handleEditSelectedMice}
      onMoveSelected={handleMoveSelectedMice}
      onTransferSelected={handleTransferSelectedMice}
      onRestoreSelected={handleRestoreSelectedMice}
      onArchiveSelected={handleDeleteSelectedMice}
      onShowImport={handleShowImportForm}
      onExportExcel={handleExportExcel}
      onOpenTransnetyx={() => navigateToSettings('transnetyx')}
     />
    )}
    </div>

    {/* Mobile Floating Action Button */}
    {isMobile && (
     <button
      className="fab"
      onClick={() => {
       setIsEditing(false);
       setMouseFormData({ physical_tag_input: '', sex: 'Male', genotype_data: [], currentLocusSelection: '', currentAlleleSelection: '', date_of_birth: '', copies: 1 });
       setSharedCageId('');
       setSlideOutContent('mouse');
       setShowSlideOutPanel(true);
       setFormError('');
      }}
      title="Add New Mouse"
     >
      +
     </button>
    )}

    {loading ? (
      <SkeletonRow count={itemsPerPageMice > 10 ? 10 : itemsPerPageMice} />
    ) : mice.length === 0 && totalMiceCount === 0 ? (
      <div className="empty-state">
        <p>No mice found.</p>
        <p>Add your first mouse using the + button, or try clearing your filters.</p>
      </div>
    ) : (
     <div className="mice-list-container" onClick={() => showFiltersMice && setShowFiltersMice(false)}>
      <MiceTable
        isMobile={isMobile}
        mice={mice}
        selectedMiceForBulkMove={selectedMiceForBulkMove}
        setSelectedMiceForBulkMove={setSelectedMiceForBulkMove}
        filtersMice={filtersMice}
        user={user}
        sortColumnMice={sortColumnMice}
        sortOrderMice={sortOrderMice}
        openMenuMouseId={openMenuMouseId}
        onEdit={handleEditMouse}
        onMove={handleMoveMouse}
        onDelete={handleDeleteMouse}
        onRestore={handleRestoreMouse}
        onViewPedigree={handleViewPedigree}
        onSort={handleSortMice}
        onSelectMouse={handleMouseCardSelect}
        onDoubleClick={handleDoubleClick}
        onOpenMenu={setOpenMenuMouseId}
      />
     </div>
    )}

    {/* Pagination Controls (desktop only) */}
    {!isMobile && totalMiceCount > 0 && (
     <div className="pagination-controls">
      <div>
       Items per page:
       <select onChange={handleItemsPerPageChangeMice} value={itemsPerPageMice}>
        {ITEMS_PER_PAGE_OPTIONS.map(option => (
         <option key={option} value={option}>{option}</option>
        ))}
       </select>
      </div>
      <div>
       <button
        onClick={() => handlePageChangeMice(currentPageMice - 1)}
        disabled={currentPageMice === 1}
       >
        Previous
       </button>
       <span>Page {currentPageMice} of {totalPagesMice}</span>
       <button
        onClick={() => handlePageChangeMice(currentPageMice + 1)}
        disabled={currentPageMice === totalPagesMice}
       >
        Next
       </button>
      </div>
     </div>
    )}
    {isMobile && isLoadingMore && (
     <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>Loading more...</div>
    )}
   </div>

   {/* Slide-out panel — portal to body so it escapes swipe-content-wrapper stacking context */}
   {createPortal(
    <div ref={slideOutPanelRef} className={`slide-out-panel ${showSlideOutPanel ? 'slide-out-panel-open' : ''}`}>
     {renderSlideOutContent()}
    </div>,
    document.body
   )}

  {/* Transfer Modal */}
  {showTransferModal && (
    <TransferModal
      selectedCount={selectedMiceForBulkMove.length}
      labMembers={labMembers}
      transferRecipientId={transferRecipientId}
      setTransferRecipientId={setTransferRecipientId}
      onConfirm={handleConfirmTransfer}
      isTransferring={isTransferringMice}
      onCancel={() => setShowTransferModal(false)}
      error={formError}
    />
  )}
  </div>
 );
}

export default MiceManagement;
